import { Search } from "lucide-react";
import { useState } from "react";

import { Select } from "@/components/ui";
import { useFindFields, type FoundField } from "@/hooks/useFindFields";
import { kindWord } from "@/lib/attributeReview/kindWords";
import { otherStores, plural } from "@/lib/attributeReview/usedBySummary";

import { KindChips, type KindOption } from "../LeafPicker";

import { FinderResult } from "./FinderResult";

type Props = {
  kinds: KindOption[];
  kind: string | null;
  splitKinds: string[];
  fallbackFolder: string | null;
  pickedPath: string | null;
  aiPaths: string[];
  store: string;
  onKind: (kind: string) => void;
  onUse: (path: string, kind: string | null) => void;
};

export function FieldFinder({
  kinds,
  kind,
  splitKinds,
  fallbackFolder,
  pickedPath,
  aiPaths,
  store,
  onKind,
  onUse,
}: Props) {
  const [text, setText] = useState("");
  const [anyKind, setAnyKind] = useState(false);
  const filter = anyKind ? "" : (kind ?? "");
  const folder = filter
    ? (kinds.find((option) => option.kind === filter)?.folder ?? fallbackFolder)
    : null;
  const q = text.trim();
  const { fields, loading } = useFindFields(q, folder);
  const head = loading
    ? "Searching…"
    : q
      ? `${plural(fields.length, "result")} for “${q}”`
      : `${filter ? `${kindWord(filter)} fields` : "All fields"} · ${fields.length}`;
  const kindOf = (field: FoundField) => {
    const inFolder = kinds.filter((option) => option.folder === field.folder);
    if (inFolder.some((option) => option.kind === kind)) return kind;
    return inFolder.length === 1 ? inFolder[0].kind : null;
  };
  const pickKind = (next: string) => {
    setAnyKind(next === "");
    if (next) onKind(next);
  };
  return (
    <>
      <div className="flex items-end gap-2">
        <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-900">
          <Search aria-hidden className="h-3.5 w-3.5 shrink-0 text-slate-500" />
          <input
            aria-label="Search fields by name"
            placeholder="Search fields by name"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-500 dark:text-slate-100"
          />
        </div>
        <label className="flex w-[42%] shrink-0 flex-col gap-1 text-xs font-medium text-slate-600 dark:text-slate-300">
          Kind (also filters the list)
          <Select
            className="w-full"
            value={filter}
            onChange={(e) => pickKind(e.target.value)}
          >
            <option value="">Any kind</option>
            {kinds.map((option) => (
              <option key={option.kind} value={option.kind}>
                {kindWord(option.kind)}
              </option>
            ))}
          </Select>
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <span>{head}</span>
        <KindChips options={splitKinds} kind={kind} onKind={pickKind} />
      </div>
      <div className="flex max-h-[220px] flex-col overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-800">
        {fields.map((field) => (
          <FinderResult
            key={field.path}
            field={field}
            current={field.path === pickedPath}
            ai={aiPaths.includes(field.path)}
            usedBy={otherStores(field.usedByStores, store)}
            onUse={() => onUse(field.path, kindOf(field))}
          />
        ))}
        {!loading && fields.length === 0 && (
          <span className="p-2.5 text-xs text-slate-500 dark:text-slate-400">
            {q
              ? `No field matches “${q}”. Try another word, or make a new field below.`
              : "No fields of this kind. Pick another kind, or make a new field below."}
          </span>
        )}
      </div>
    </>
  );
}
